import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Gift, LogOut, FileText, Plus, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

export default function ClientDashboard() {
  const [bookings, setBookings] = useState([]);
  const [giftOrders, setGiftOrders] = useState([]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchBookings();
    fetchGiftOrders();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await api.getBookings();
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const fetchGiftOrders = async () => {
    try {
      const response = await api.getGiftOrders();
      setGiftOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch gift orders:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          {/* Top Row - Logo and User Info */}
          <div className="flex items-center justify-between mb-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-12 w-auto"
              />
              <h1 className="luxury-text text-slate-900 text-xl font-semibold text-midnight-navy">Client Dashboard</h1>
            </Link>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-800 font-medium">Welcome, {user?.name}</span>
              <Button onClick={handleLogout} className="bg-slate-200 hover:bg-slate-300 text-slate-900 font-semibold" size="sm" data-testid="client-logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
          
          {/* Bottom Row - Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/bulk-rfp/new">
              <Button className="bg-gold hover:bg-gold-light text-navy-900 font-semibold shadow-md" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Bulk RFP
              </Button>
            </Link>
            <Link to="/invitation-builder">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-md" size="sm">
                <Mail className="w-4 h-4 mr-1" /> Create Invitation
              </Button>
            </Link>
            <Link to="/client/rfps/create">
              <Button className="bg-navy hover:bg-navy-700 text-white font-semibold shadow-md" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Create RFP
              </Button>
            </Link>
            <Link to="/client/rfps">
              <Button variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white font-semibold" size="sm">
                <FileText className="w-4 h-4 mr-1" /> RFP & Bidding
              </Button>
            </Link>
            <Link to="/dashboard/client/rfps">
              <Button variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white font-semibold" size="sm">
                <FileText className="w-4 h-4 mr-1" /> My Bulk RFPs
              </Button>
            </Link>
            <Link to="/event-permissions">
              <Button variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white font-semibold" size="sm">
                <FileText className="w-4 h-4 mr-1" /> Event Permits
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <Tabs defaultValue="bookings">
          <TabsList>
            <TabsTrigger value="bookings" data-testid="bookings-tab">My Bookings</TabsTrigger>
            <TabsTrigger value="gifts" data-testid="gifts-tab">Gift Orders</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-8">
            <div className="space-y-4">
              {bookings.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-slate-200 shadow-sm">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-gold" />
                  <h3 className="text-xl font-semibold text-navy mb-2">No bookings yet</h3>
                  <p className="text-slate-800 mb-6">Start exploring premium properties for your corporate events</p>
                  <Button 
                    onClick={() => navigate('/properties')} 
                    className="bg-gold hover:bg-gold-light text-navy-900 font-semibold px-8 py-6 text-base shadow-lg"
                    data-testid="browse-properties-button"
                  >
                    Browse Properties
                  </Button>
                </div>
              ) : (
                bookings.map((booking) => (
                  <div key={booking.id} className="bg-white p-6 rounded-sm border border-slate-200" data-testid={`booking-card-${booking.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="luxury-text text-slate-900 text-xl font-medium mb-2">{booking.property_name}</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm text-slate-800">
                          <div>
                            <span className="font-semibold">Event:</span> {booking.event_type}
                          </div>
                          <div>
                            <span className="font-semibold">Dates:</span> {new Date(booking.start_date).toLocaleDateString()} - {new Date(booking.end_date).toLocaleDateString()}
                          </div>
                          <div>
                            <span className="font-semibold">Guests:</span> {booking.guests}
                          </div>
                          <div>
                            <span className="font-semibold">Amount:</span> ₹{booking.total_amount.toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`px-3 py-1 rounded-sm text-xs font-semibold ${
                          booking.status === 'confirmed' ? 'bg-emerald-100 text-emerald-800' : 
                          booking.status === 'pending' ? 'bg-amber-100 text-amber-800' : 
                          'bg-slate-100 text-slate-800'
                        }`}>
                          {booking.status.toUpperCase()}
                        </span>
                        <span className={`px-3 py-1 rounded-sm text-xs font-semibold ${
                          booking.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : 
                          'bg-amber-100 text-amber-800'
                        }`}>
                          Payment: {booking.payment_status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="gifts" className="mt-8">
            <div className="space-y-4">
              {giftOrders.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-slate-200 shadow-sm">
                  <Gift className="w-16 h-16 mx-auto mb-4 text-gold" />
                  <h3 className="text-xl font-semibold text-navy mb-2">No gift orders yet</h3>
                  <p className="text-slate-800 mb-6">Explore our premium corporate gifting collection</p>
                  <Button 
                    onClick={() => navigate('/gifts')} 
                    className="bg-gold hover:bg-gold-light text-navy-900 font-semibold px-8 py-6 text-base shadow-lg"
                    data-testid="browse-gifts-button"
                  >
                    Browse Gifts
                  </Button>
                </div>
              ) : (
                giftOrders.map((order) => (
                  <div key={order.id} className="bg-white p-6 rounded-sm border border-slate-200" data-testid={`gift-order-card-${order.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="luxury-text text-slate-900 text-xl font-medium mb-2">{order.gift_name}</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm text-slate-800">
                          <div>
                            <span className="font-semibold">Quantity:</span> {order.quantity}
                          </div>
                          <div>
                            <span className="font-semibold">Total:</span> ₹{order.total_amount.toLocaleString()}
                          </div>
                          <div>
                            <span className="font-semibold">Recipient:</span> {order.delivery_name}
                          </div>
                          <div>
                            <span className="font-semibold">Phone:</span> {order.delivery_phone}
                          </div>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-sm text-xs font-semibold ${
                        order.status === 'confirmed' ? 'bg-emerald-100 text-emerald-800' : 
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {order.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
